import React, { useEffect, useRef, useState } from "react";
import "../../../../../css/Recruitment/Sections.css";
import MobileNumberInput from "../../../../DesignSystem/Inputs/MobileNumberInput";
import { writeDataS5 } from "../../../../../backend/Recruitment/Sections/Section5/writeDataS5";
import { readDataS5 } from "../../../../../backend/Recruitment/Sections/Section5/readDataS5";
import errorTrackerV2 from "../Validation/Utils/ErrorTrackerV2";
import YesNoRadio from "../RadioButton";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";
import { auth } from "../../../../../backend/firebaseConfig";
import { validateSection5 } from "../Validation/section5";
import InputField from "../../../../DesignSystem/Inputs/InputField";
import DateField from "../../../../DesignSystem/Inputs/DateField";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import { formatDateValue } from "../Utils/dateFormatting";

const defaultTradeReferees = [
  {
    tradeName: "",
    companyName: "",
    tradeAddress: "",
    tradeEmailAddress: "",
    refereePhoneNumber: "",
    datesOfTradingActivities: "",
  },
  {
    tradeName: "",
    companyName: "",
    tradeAddress: "",
    tradeEmailAddress: "",
    refereePhoneNumber: "",
    datesOfTradingActivities: "",
  },
];

const defaultPersonalReferees = [
  {
    refereeName: "",
    refereeAddress: "",
    refereePhoneNumber: "",
    datesOfUnemployment: "",
    refereeEmailAddress: "",
    relationshipToReferee: "",
  },
  {
    refereeName: "",
    refereeAddress: "",
    refereePhoneNumber: "",
    datesOfUnemployment: "",
    refereeEmailAddress: "",
    relationshipToReferee: "",
  },
];

const coerceBool = (v) =>
  v === true || v === false ? v : String(v ?? "").toLowerCase() === "true";

const safeDate = (v) => {
  if (!v) return "";
  if (v instanceof Date) return v;
  if (typeof v === "object" && v?.seconds) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d;
};

const toArray = (v, fallback) =>
  Array.isArray(v)
    ? v
    : v && typeof v === "object"
      ? Object.values(v)
      : fallback;


const Section5 = ({ onValidationStatusChange }) => {
  const sectionTopRef = useRef(null);
  const [formData, setFormData] = useState({
    hasAccountant: null,
    title: "",
    surname: "",
    initials: "",
    telephone: "",
    emailAddress: "",
    address: "",
    from: "",
    tradeReferees: defaultTradeReferees,
    personalReferees: defaultPersonalReferees,
  });

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());

  // submission listener (stable deps)
  useEffect(() => {
    const remove = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => remove();
  }, []);

  // load saved data
  useEffect(() => {
    (async () => {
      const data = await readDataS5();
      // console.log("Section 5: Data loaded from Firebase:", data);
      if (!data) {
        // console.log("Section 5: No data found");
        return;
      }
      // console.log(
      //   "Section 5: Setting form data, validation will trigger automatically",
      // );

      const fromDate = data.from?.seconds
        ? new Date(data.from.seconds * 1000)
        : "";

      setFormData((prev) => ({
        ...prev,
        ...data,
        hasAccountant:
          data.hasAccountant === null || data.hasAccountant === undefined
            ? null
            : coerceBool(data.hasAccountant),
        from: safeDate(data.from),

        tradeReferees: toArray(data.tradeReferees, defaultTradeReferees),
        personalReferees: toArray(
          data.personalReferees,
          defaultPersonalReferees,
        ),
      }));
    })();
  }, []);

  // single source validation
  const runValidation = (nextData) => {
    const nextErrors = validateSection5(nextData); // errors-only
    setErrors(nextErrors);

    const ok = Object.keys(nextErrors).length === 0;

    // IMPORTANT: tracker stores ONLY error fields (no _isValid)
    errorTrackerV2.setSectionErrors("section5", nextErrors);

    onValidationStatusChange?.(ok);
    return ok;
  };

  useEffect(() => {
    runValidation(formData);
  }, [formData]);

  const isValid = Object.keys(errors).length === 0;

  // handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleNestedChange = (index, eOrValue, sectionName, fieldOverride) => {
    // supports both <input onChange> and PhoneInput onChange(value)
    const name = fieldOverride || eOrValue?.target?.name;
    const value = fieldOverride ? eOrValue : eOrValue?.target?.value;

    setFormData((prev) => {
      const updated = [...(prev[sectionName] || [])];
      updated[index] = { ...(updated[index] || {}), [name]: value };
      return { ...prev, [sectionName]: updated };
    });
  };

  const handleYesNoChange = (name, e) => {
    const raw = e?.target?.value;
    const bool =
      raw === true || raw === false
        ? raw
        : String(raw).toLowerCase() === "true";
    setFormData((prev) => ({ ...prev, [name]: bool }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });
    const ok = runValidation(formData);

    // Save data regardless of validation status
    writeDataS5(formData);

    // Show appropriate alert based on validation result
    if (ok ) {
      setAlertMessage("Data saved successfully!");
      setAlertType("success");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  const capitalizeFirstLetter = (s) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

  const tradeRefereeFields = [
    "tradeName",
    "companyName",
    "tradeAddress",
    "tradeEmailAddress",
    "refereePhoneNumber",
    "datesOfTradingActivities",
  ];

  const personalRefereeFields = [
    "refereeName",
    "refereeAddress",
    "refereePhoneNumber",
    "refereeEmailAddress",
    "relationshipToReferee",
    "datesOfUnemployment",
  ];

  const renderReferees = (referees, sectionName) => {
    const fieldOrder =
      sectionName === "personalReferees"
        ? personalRefereeFields
        : tradeRefereeFields;
    
    const singularLabel = (name) =>
      capitalizeFirstLetter(
        name
          .replace(/Referees$/, " Referee") // personalReferees -> personal Referee
          .replace(/([A-Z])/g, " $1") // camelCase -> spaced
          .trim(),
      );

    return (
      <div className="form-row">
        {referees.map((referee, index) => (
          <>
            <div className="Content">
              {singularLabel(sectionName)} {index + 1}
            </div>
            <div key={index} className="input-group input-group--grid-2col">
              {fieldOrder.map((field) =>
                field.toLowerCase().includes("phone") ? (
                  <MobileNumberInput
                    key={field}
                    id={`${field}-${index}-${sectionName}`}
                    // label={capitalizeFirstLetter(
                    //   field.replace(/([A-Z])/g, " $1").trim(),
                    // )}
                    value={referee[field] || ""}
                    onChange={(val) =>
                      handleNestedChange(index, val || "", sectionName, field)
                    }
                    error={errors[`${field}_${index}_${sectionName}`] || ""}
                  />
                ) : (
                  <InputField
                    key={field}
                    name={field}
                    placeholder={
                      field.toLowerCase().includes("refereename") ||
                      field.toLowerCase().includes("tradename")
                        ? "Full Name"
                        : field.toLowerCase().includes("datesofunemployment")
                          ? "Dates Known"
                          : capitalizeFirstLetter(
                              field.replace(/([A-Z])/g, " $1").trim(),
                            )
                    }
                    value={referee[field] || ""}
                    onChange={(e) => handleNestedChange(index, e, sectionName)}
                    isTextArea={
                      (field.includes("Address") ||
                        field.toLowerCase().includes("dates")) &&
                      !field.includes("Email")
                    }
                    error={errors[`${field}_${index}_${sectionName}`]}
                  />
                ),
              )}
            </div>
          </>
        ))}
      </div>
    );
  };

  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertMessage("")}
        />
      )}
      <h3>Section 5 - Applicant Details</h3>
      <div className="Content">
        5.1 Accountant details
        <span style={{ color: "var(--Error)" }}>*</span>
      </div>
      <div className="input-group-row">
        <div className="Content">
          Do you currently have an accountant?
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <YesNoRadio
          name="hasAccountant"
          value={formData.hasAccountant}
          onChange={(e) => handleYesNoChange("hasAccountant", e)} // boolean stored
          error={errors.hasAccountant}
        />
      </div>

      {formData.hasAccountant === true && (
        <>
          <div className="input-group input-group--grid-2col">
            <div
              style={{ gap: "10px", flexDirection: "column", display: "flex" }}
            >
              <InputField
                name="title"
                placeholder="Title"
                value={formData.title}
                onChange={handleChange}
                error={showErrors && !!errors.title}
              />
              <InputField
                name="initials"
                placeholder="Forename"
                value={formData.initials}
                onChange={handleChange}
                error={showErrors && !!errors.initials}
              />
              <InputField
                name="surname"
                placeholder="Surname"
                value={formData.surname}
                onChange={handleChange}
                error={showErrors && !!errors.surname}
              />
            </div>
            <InputField
              name="address"
              placeholder="Address"
              value={formData.address}
              onChange={handleChange}
              error={showErrors && !!errors.address}
              multiline
              rows={4}
            />

            <InputField
              name="emailAddress"
              placeholder="Email Address"
              label={"Email Address"}
              value={formData.emailAddress}
              onChange={handleChange}
              error={showErrors && !!errors.emailAddress}
            />

            <MobileNumberInput
              id="telephone"
              label="Telephone"
              value={formData.telephone}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, telephone: val || "" }))
              }
              error={showErrors && !!errors.telephone}
            />
            {/* {errors.telephone ? (
              <div className="error-text">{errors.telephone}</div>
            ) : null} */}
          </div>

          <div className="input-group-row">
            <div className="Content">
              How long has the above been your accountant?
            </div>
            <div className="input-group">
              <DateField
                name="from"
                label="From"
                value={formData.from}
                onChange={(e) => {
                  const dateValue = e.target.value
                    ? new Date(e.target.value)
                    : "";
                  setFormData((prev) => ({ ...prev, from: dateValue }));
                }}
                error={showErrors && !!errors.from}
                helperText={showErrors ? errors.from : ""}
              />
            </div>
          </div>
        </>
      )}

      <div className="separator" />

      <div className="Content">
        5.2 Trade Referees
        <br />
        If you have been self-employed within the last 3 years in an industry
        which is not within the financial services sector, please provide the
        names and addresses of 2 Trade Referees who can verify your trading
        activities
      </div>
      {renderReferees(formData.tradeReferees, "tradeReferees")}

      <div className="separator" />

      <div className="Content-Bold-Underline">5.3 Personal Referees</div>
      <div className="Content">
        Please note these Personal Referees cannot be family members or
        individuals with a relationship to Rosemount.
      </div>
      <div className="Content">
        If you have any gaps in employment within the last 3 years, please
        provide the names and addresses of 2 Personal Referees.
      </div>
      {renderReferees(formData.personalReferees, "personalReferees")}

      {!isSubmitted && <PrimaryButton label="SAVE" onClick={handleSubmit} />}
    </form>
  );
};

export default Section5;
